import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CustomFieldDefinitionFunnelsAddBodyParams,
  type CustomFieldDefinitionFunnelsGetResponse,
  type CustomFieldDefinitionFunnelsUpdateBodyParams,
} from 'src/api/generated';
import { CrmCustomFieldsDefinitionsFunnelsService } from 'src/api/generated/services/CrmCustomFieldsDefinitionsFunnelsService';

const qk = (definitionId?: number) => [
  'crm/custom-fields/definition-funnels',
  definitionId,
];

export const useGetDefinitionFunnels = (
  definitionId?: number,
  enabled: boolean = true
) => {
  const fetcher =
    async (): Promise<CustomFieldDefinitionFunnelsGetResponse> => {
      if (!definitionId) {
        return {
          definitionId: 0,
          funnelIds: [],
        } as CustomFieldDefinitionFunnelsGetResponse;
      }
      return await CrmCustomFieldsDefinitionsFunnelsService.getCustomFieldDefinitionFunnels(
        definitionId
      );
    };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk(definitionId),
    queryFn: fetcher,
    enabled: enabled && typeof definitionId === 'number' && definitionId > 0,
  });

  return {
    data,
    loading: isLoading,
    error: (error as Error) ?? null,
    refetch,
  };
};

export const useReplaceDefinitionFunnels = (
  definitionId?: number,
  opts?: {
    onSuccess?: (res: void) => void;
    onError?: (err: Error) => void;
  }
) => {
  const queryClient = useQueryClient();
  const mutationFn = async (
    vars: number[] | { funnelIds: number[]; definitionIdOverride?: number }
  ): Promise<void> => {
    const funnelIds = Array.isArray(vars) ? vars : vars.funnelIds;
    const idOverride = Array.isArray(vars)
      ? undefined
      : vars.definitionIdOverride;
    const effectiveId = idOverride ?? definitionId;
    if (!effectiveId) {
      return;
    }
    const body: CustomFieldDefinitionFunnelsUpdateBodyParams = { funnelIds };
    return await CrmCustomFieldsDefinitionsFunnelsService.updateCustomFieldDefinitionFunnels(
      effectiveId,
      body
    );
  };

  const { mutateAsync, isPending, error } = useMutation<
    void,
    Error,
    number[] | { funnelIds: number[]; definitionIdOverride?: number },
    { prevIds: number[]; nextIds: number[] }
  >({
    mutationFn,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: qk(definitionId) });
      const prev =
        queryClient.getQueryData<CustomFieldDefinitionFunnelsGetResponse>(
          qk(definitionId)
        );
      const nextIds = Array.isArray(vars) ? vars : vars.funnelIds;
      return { prevIds: prev?.funnelIds ?? [], nextIds };
    },
    onSuccess: (_res, _vars, ctx) => {
      const ids = new Set([...(ctx?.prevIds ?? []), ...(ctx?.nextIds ?? [])]);
      ids.forEach((fid) => {
        queryClient.invalidateQueries({
          queryKey: ['crm/funnels/custom-field-definitions', fid],
        });
      });
      queryClient.invalidateQueries({ queryKey: qk(definitionId) });
      opts?.onSuccess?.(_res);
    },
    onError: (err) => opts?.onError?.(err as Error),
  });

  return {
    replaceFunnels: mutateAsync,
    loading: isPending,
    error: (error as Error) ?? null,
  };
};

export const useAddDefinitionFunnels = (
  definitionId?: number,
  opts?: {
    onSuccess?: (res: void) => void;
    onError?: (err: Error) => void;
  }
) => {
  const queryClient = useQueryClient();
  const mutationFn = async (funnelIds: number[]): Promise<void> => {
    if (!definitionId || funnelIds.length === 0) return;
    const body: CustomFieldDefinitionFunnelsAddBodyParams = { funnelIds };
    return await CrmCustomFieldsDefinitionsFunnelsService.addCustomFieldDefinitionFunnels(
      definitionId,
      body
    );
  };

  const { mutateAsync, isPending, error } = useMutation<
    void,
    Error,
    number[],
    { prevIds: number[]; nextIds: number[] }
  >({
    mutationFn,
    onMutate: async (funnelIds) => {
      await queryClient.cancelQueries({ queryKey: qk(definitionId) });
      const prev =
        queryClient.getQueryData<CustomFieldDefinitionFunnelsGetResponse>(
          qk(definitionId)
        );
      return { prevIds: prev?.funnelIds ?? [], nextIds: funnelIds ?? [] };
    },
    onSuccess: (_res, _vars, ctx) => {
      const ids = new Set([...(ctx?.prevIds ?? []), ...(ctx?.nextIds ?? [])]);
      ids.forEach((fid) =>
        queryClient.invalidateQueries({
          queryKey: ['crm/funnels/custom-field-definitions', fid],
        })
      );
      queryClient.invalidateQueries({ queryKey: qk(definitionId) });
      opts?.onSuccess?.(_res);
    },
    onError: (err) => opts?.onError?.(err as Error),
  });

  return {
    addFunnels: mutateAsync,
    loading: isPending,
    error: (error as Error) ?? null,
  };
};

export const useRemoveDefinitionFunnel = (
  definitionId?: number,
  opts?: {
    onSuccess?: (res: void) => void;
    onError?: (err: Error) => void;
  }
) => {
  const queryClient = useQueryClient();
  const mutationFn = async (
    vars: number | { funnelId: number; definitionIdOverride?: number }
  ): Promise<void> => {
    const funnelId = typeof vars === 'number' ? vars : vars.funnelId;
    const idOverride =
      typeof vars === 'number' ? undefined : vars.definitionIdOverride;
    const effectiveId = idOverride ?? definitionId;
    if (!effectiveId || !funnelId) {
      return;
    }
    return await CrmCustomFieldsDefinitionsFunnelsService.removeCustomFieldDefinitionFunnel(
      effectiveId,
      funnelId
    );
  };

  const { mutateAsync, isPending, error } = useMutation<
    void,
    Error,
    number | { funnelId: number; definitionIdOverride?: number },
    { prevIds: number[]; removedId?: number }
  >({
    mutationFn,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: qk(definitionId) });
      const prev =
        queryClient.getQueryData<CustomFieldDefinitionFunnelsGetResponse>(
          qk(definitionId)
        );
      const removedId = typeof vars === 'number' ? vars : vars.funnelId;
      return { prevIds: prev?.funnelIds ?? [], removedId };
    },
    onSuccess: (res, _vars, ctx) => {
      const ids = new Set(
        [...(ctx?.prevIds ?? []), ctx?.removedId].filter(Boolean) as number[]
      );
      ids.forEach((fid) =>
        queryClient.invalidateQueries({
          queryKey: ['crm/funnels/custom-field-definitions', fid],
        })
      );
      queryClient.invalidateQueries({ queryKey: qk(definitionId) });
      opts?.onSuccess?.(res);
    },
    onError: (err) => opts?.onError?.(err as Error),
  });

  return {
    removeFunnel: mutateAsync,
    loading: isPending,
    error: (error as Error) ?? null,
  };
};
